import { taskLabel } from "@/lib/task-reference";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { type CarrierTask, useCarrierTask } from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { RowMenu, RowMenuItem } from "@/components/ui/row-menu";
import {
  formatTaskPickupWindow,
  formatTaskReadyAt,
  formatTaskScheduleDay,
} from "@/features/task-schedule-display";
import { hasHttpStatus } from "@/lib/api-error";
import { formatDate } from "@/lib/format";
import { isTaskOnCancellationHold } from "@/lib/cancellation-work-hold";
import { taskInstructions } from "@/lib/task-instructions";

interface TaskDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string | null;
  summary?: CarrierTask;
  onComplete: (task: CarrierTask) => void;
  onAssignTrip: (task: CarrierTask) => void;
  onUnassignTrip: (task: CarrierTask) => void;
  onReassign: (task: CarrierTask) => void;
  onAssignOperator: (task: CarrierTask) => void;
  actionsPending?: boolean;
}

function personName(
  person:
    | {
        name: string | null;
        email: string | null;
      }
    | null
    | undefined
): string {
  if (!person) return "Unassigned operator";
  return person.name || person.email || "Unassigned operator";
}

function address(task: CarrierTask): string {
  return [
    task.addressLine1 ?? task.address?.line1,
    task.addressLine2,
    task.addressCity ?? task.address?.city,
    task.addressProvince ?? task.address?.province,
    task.addressPostalCode,
    task.addressCountry,
  ]
    .filter(Boolean)
    .join(", ");
}

function canComplete(task: CarrierTask): boolean {
  return (
    !isTaskOnCancellationHold(task) &&
    (task.status === "PENDING" || task.status === "IN_PROGRESS")
  );
}

export function TaskDetailDialog({
  open,
  onOpenChange,
  taskId,
  summary,
  onComplete,
  onAssignTrip,
  onUnassignTrip,
  onReassign,
  onAssignOperator,
  actionsPending = false,
}: TaskDetailDialogProps) {
  const query = useCarrierTask(open ? taskId : null);
  const task = query.data ?? summary;

  return (
    <DetailDrawer
      open={open}
      onOpenChange={(nextOpen) => {
        if (!actionsPending) onOpenChange(nextOpen);
      }}
      title={task ? taskLabel(task) : "Task detail"}
      description={
        task
          ? `${humanize(task.type)} · ${humanize(task.status)}`
          : "Complete carrier-visible Task context"
      }
      widthClassName="sm:max-w-xl"
    >
      {query.isLoading && !task && (
        <div
          role="status"
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading Task…
        </div>
      )}

      {query.error && !task && (
        <div role="alert" className="space-y-3 text-sm text-destructive">
          <p>
            {hasHttpStatus(query.error, 403)
              ? "You do not have permission to view this Task."
              : "Task detail could not be loaded."}
          </p>
          {!hasHttpStatus(query.error, 403) && (
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={() => query.refetch()}
            >
              Retry
            </Button>
          )}
        </div>
      )}

      {task && (
        <div className="space-y-5 text-sm">
          {query.isLoading && (
            <p role="status" className="text-xs text-muted-foreground">
              Refreshing complete Task detail…
            </p>
          )}
          {query.error && (
            <p
              role="alert"
              className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900"
            >
              Task detail could not be refreshed. Cached Task context remains
              visible.
            </p>
          )}

          <div className="flex items-center justify-between gap-3">
            <StatusBadge status={task.status} />
            <StatusBadge status={task.type} />
          </div>

          {isTaskOnCancellationHold(task) && (
            <p className="rounded-md border bg-muted/50 p-3 font-medium text-muted-foreground">
              On hold · cancellation review. Assignment and completion actions
              are frozen until an owner or dispatcher resolves the request.
            </p>
          )}

          <Section title="Identity and timing">
            <DefinitionList>
              <Field label="Task ID" mono>
                {task.id}
              </Field>
              <Field label="Created">{formatDate(task.createdAt)}</Field>
              <Field label="Updated">{formatDate(task.updatedAt)}</Field>
              <Field label="Scheduled date">
                {formatTaskScheduleDay(task)}
              </Field>
              {task.scheduledWindow && (
                <Field label="Pickup window">
                  {formatTaskPickupWindow(task)}
                </Field>
              )}
              <Field label="Ready at">{formatTaskReadyAt(task)}</Field>
              <Field label="Started">{formatDate(task.startedAt)}</Field>
              <Field label="Completed">{formatDate(task.completedAt)}</Field>
              <Field label="Received">{formatDate(task.receivedAt)}</Field>
              <Field label="Description">{task.description ?? "-"}</Field>
              <Field label="Failure reason">
                {task.failureReason ?? "-"}
                {task.failureReasonCode !== null &&
                task.failureReasonCode !== undefined
                  ? ` (${task.failureReasonCode})`
                  : ""}
              </Field>
            </DefinitionList>
          </Section>

          <Section title="Assignment">
            <DefinitionList>
              <Field label="Dispatch Center">
                {task.dispatchCenter?.name ??
                  task.depot?.name ??
                  "Unknown Dispatch Center"}
              </Field>
              <Field label="Trip">
                {task.trip
                  ? `TRP-${task.trip.tripNumber} · ${humanize(task.trip.status)}`
                  : "No Trip"}
              </Field>
              <Field label="Operator">
                {personName(
                  task.operator?.carrierUser ?? task.trip?.operator?.carrierUser
                )}
              </Field>
              <Field label="Waypoint ID" mono>
                {task.waypointId ?? "No waypoint"}
              </Field>
            </DefinitionList>
          </Section>

          <Section title="Shipment">
            <DefinitionList>
              <Field label="Shipment">
                {task.shipment
                  ? `SHP-${task.shipment.shipmentNumber}`
                  : "No Shipment"}
              </Field>
              <Field label="Tracking">
                {task.shipment?.trackingNumber ?? "No tracking number"}
              </Field>
              <Field label="Shipment status">
                {task.shipment?.status
                  ? humanize(task.shipment.status)
                  : "Unknown shipment status"}
              </Field>
              <Field label="Carrier">
                {task.shipment?.carrier?.name ??
                  task.shipment?.carrier?.code ??
                  "Unknown carrier"}
              </Field>
              <Field label="Order ID" mono>
                {task.shipment?.orderId ?? "No order"}
              </Field>
              <Field label="Snapshot" mono>
                {formatStructured(task.shipmentSnapshot)}
              </Field>
            </DefinitionList>
          </Section>

          <Section title="Contact and location">
            <DefinitionList>
              <Field label="Name">{task.contactName ?? "-"}</Field>
              <Field label="Company">{task.contactCompany ?? "-"}</Field>
              <Field label="Phone">{task.contactPhone ?? "-"}</Field>
              <Field label="Email">{task.contactEmail ?? "-"}</Field>
              <Field label="Address">{address(task) || "-"}</Field>
              <Field label="Instructions">
                {taskInstructions(task) ?? "-"}
              </Field>
              <Field label="Location" mono>
                {formatStructured(task.address?.location)}
              </Field>
            </DefinitionList>
          </Section>

          <Section title="Dependencies and notes">
            <DefinitionList>
              <Field label="Parent">
                {task.parentTask
                  ? `TSK-${task.parentTask.taskNumber} · ${humanize(task.parentTask.status)}`
                  : "No parent Task"}
              </Field>
              <Field label="Children">
                {task.childTasks?.length
                  ? task.childTasks
                      .map(
                        (child) =>
                          `TSK-${child.taskNumber} · ${humanize(child.status)}`
                      )
                      .join(", ")
                  : "No child Tasks"}
              </Field>
              {task.type !== "CUSTOMER_DELIVERY" && (
                <Field label="Notes">{task.notes ?? "-"}</Field>
              )}
            </DefinitionList>
          </Section>

          <Section title="Proof">
            {task.proof ? (
              <div className="space-y-2">
                <p>{task.proof.notes ?? "Proof captured."}</p>
                <p className="text-xs text-muted-foreground">
                  Captured {formatDate(task.proof.createdAt)}
                </p>
                {task.proof.mediaItems.length > 0 && (
                  <ul className="divide-y rounded-md border">
                    {task.proof.mediaItems.map((item) => (
                      <li
                        key={item.id}
                        className="flex min-h-11 items-center p-2"
                      >
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          className="break-all text-sm text-primary underline-offset-4 hover:underline"
                        >
                          {humanize(item.type)} proof
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <p>-</p>
            )}
          </Section>

          <div className="sticky bottom-0 grid grid-cols-[1fr_44px] gap-2 border-t bg-background py-3 md:grid-cols-[1fr_32px]">
            <Button
              type="button"
              size="touch"
              variant={task.tripId ? "outline" : "default"}
              disabled={actionsPending || isTaskOnCancellationHold(task)}
              onClick={() =>
                task.tripId ? onUnassignTrip(task) : onAssignTrip(task)
              }
            >
              {isTaskOnCancellationHold(task)
                ? "Cancellation review hold"
                : task.tripId
                  ? "Unassign from trip"
                  : "Assign to trip"}
            </Button>
            <RowMenu
              label={`Actions for ${taskLabel(task)}`}
              disabled={actionsPending || isTaskOnCancellationHold(task)}
              modal
            >
              {canComplete(task) && (
                <RowMenuItem onClick={() => onComplete(task)}>
                  Complete Task
                </RowMenuItem>
              )}
              <RowMenuItem onClick={() => onReassign(task)}>
                Reassign Dispatch Center
              </RowMenuItem>
              {!task.tripId && canComplete(task) && (
                <RowMenuItem onClick={() => onAssignOperator(task)}>
                  {task.operatorId ? "Reassign Operator" : "Assign Operator"}
                </RowMenuItem>
              )}
            </RowMenu>
          </div>
        </div>
      )}
    </DetailDrawer>
  );
}

function Field({
  label,
  children,
  mono = false,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? "break-all font-mono text-xs" : "break-words"}>
        {children}
      </dd>
    </>
  );
}

function DefinitionList({ children }: { children: ReactNode }) {
  return (
    <dl className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-4 gap-y-2">
      {children}
    </dl>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 border-t pt-4 first:border-t-0 first:pt-0">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function humanize(value: string) {
  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatStructured(value: unknown) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return "Unavailable";
  }
}
